<template>
  <section v-if="activeLivingOption" class="row">
    <card>
      <div class="d-flex flex-wrap">
        <div class="col-12 mb-4">
          <div class="row">
            <div class="col-12">
              <h3>Editing {{ activeLivingOption.label }}</h3>
              <button class="btn btn-sm btn-primary" @click="viewAll">View all living options</button>
              <button class="btn btn-sm btn-primary ml-1" @click="saveLivingOption(activeLivingOption)">Save</button>
            </div>
          </div>
        </div>
  
        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Label
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeLivingOption.label"
                placeholder="i.e. Juman One"
              />
            </div>
            <div class="col-3 mb-4">
              Is Sold Out <br>
              <toggle-button
                v-model="activeLivingOption.isSoldOut" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-3 mb-4">
              Is Tag and Slide Hidden <br>
              <toggle-button
                v-model="activeLivingOption.isVisuallyHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-3 mb-4">
              Is Filter Slideout Hidden <br>
              <toggle-button
                v-model="activeLivingOption.isFilterHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-12 mb-4">
              Sidebar Description
              <ck-editor v-model="activeLivingOption.description" id="livingOptionDescription"></ck-editor>
            </div>
            <div class="col-12 mb-4">
              Background Image
              <input-asset v-model="activeLivingOption.backgroundImage" :multiple="false"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Living Option Logo
              <input-asset v-model="activeLivingOption.logo"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Card Background Image
              <input-asset v-model="activeLivingOption.cardBackgroundImage"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Sidebar Minimap Image
              <input-asset v-model="activeLivingOption.sidebarMinimap"></input-asset>
            </div>
          </div>
        </div>
  
        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Associated Subsections
              <input-multi-select
                v-model="activeLivingOption.subsectionIds"
                :options="subsectionOptions"
              ></input-multi-select>
            </div>
            <div class="col-12 mb-4">
              Subsection Type
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeLivingOption.subsectionType"
                placeholder="i.e. Floor, Release, etc."
              />
            </div>
            <div class="col-12 mb-4">
              Videos (displayed on property favourites webpage)
              <input-asset v-model="activeLivingOption.videos" :multiple="true"></input-asset>
            </div>
            <div class="col-12 mb-4">
              Brochure (displayed on property favourites webpage)
              <input-asset v-model="activeLivingOption.brochure"></input-asset>
            </div>
          </div>
        </div>
  
        <div class="col-12 mb-4">
          <label>Tags</label>
          <button class="btn btn-sm btn-primary my-2" @click="addLivingOptionTag">add tag</button>
          <draggable-elements 
            :tags="activeLivingOption.tags"
            :subsection-ids="activeLivingOption.subsectionIds"
            :background-image="activeLivingOption.backgroundImage"
          />
        </div>
      </div>
    </card>
  </section>
</template>
<script>
import { includes, filter } from 'lodash'
import { mapGetters } from 'vuex'
import { FETCH_CONFIG_ITEMS, CONFIG_ITEMS_EDIT } from '@/store/actions.type'
import CkEditor from '@/components/CKEditor'
import DotDropper from '@/components/DotDropper'
import InputAsset from '@/components/InputAsset'
import InputMultiSelect from '@/components/InputMultiSelect'
import LivingOptionTags from '@/components/LivingOptionTags'
import LivingOptionMarkers from '@/components/LivingOptionMarkers'
import InfoBox from '@/components/InfoBox'
import DraggableElements from '@/components/DraggableElements'

export default {
  components: { CkEditor,
    DotDropper,
    InputAsset,
    InputMultiSelect,
    LivingOptionTags,
    LivingOptionMarkers,
    InfoBox,
    DraggableElements
  },

  data () {
    return {
      configItems: [],
      liveHereSidebarMinimap: '',
      liveHereDescription: {
        en: ''
      },
      liveHereHeading: {
        en: ''
      },
      liveHereBackground: '',
      livingOptionsConfigItemsKeys: [
        'liveHereSidebarMinimap',
        'liveHereHeading',
        'liveHereDescription',
        'liveHereBackground'
      ],

      activeLivingOptionId: null,
      livingOptionsFilterText: '',
      filterActiveStates: []
    }
  },

  computed: {
    ...mapGetters([
      'livingOptions',
      'subsections',
    ]),

    activeLivingOption () {
      return this.livingOptions.find(livingOption => livingOption._id === this.activeLivingOptionId)
    },

    subsectionOptions () {
      let subsectionOptions = []
      this.subsections.forEach(subsection => {
        const isNotUsedLivingOption = subsection.parent.id === null || subsection.parent.id === this.activeLivingOption._id // is not being used by a different living option
        const isNotUsedSubsection = subsection.parent.type !== 'subsection' // is not being used by a subsection

        if (isNotUsedLivingOption && isNotUsedSubsection) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id
          })
        }
      })
      return subsectionOptions
    },

    liveOptionConfigTags () {
      return this.configItems.find(item => item.key === 'liveHereMasterplanTags')
    },

    filteredLivingOptions () {
      let filtered = filter(this.livingOptions, item => {
        return item.label.toLowerCase().includes(this.livingOptionsFilterText.toLowerCase())
      })

      if (this.filterActiveStates.length) {
        filtered = filter(filtered, item => {
          return (this.filterActiveStates.includes('Active') && item.active) || (this.filterActiveStates.includes('Inactive') && !item.active)
        })
      }

      return filtered
    },   
  },

  created () {
    this.$store.dispatch('FETCH_ASSETS')
    this.$store.dispatch('FETCH_LIVING_OPTIONS')
    this.$store.dispatch('FETCH_SUBSECTIONS')
    this.setData()
  },

  mounted () {
    this.activeLivingOptionId = this.$route.params.id
  },

  methods: {
    viewAll () {
      this.$router.push('/live/masterplan')
    },

    saveConfig () {
      this.configItems.forEach(configItem => {
        this.livingOptionsConfigItemsKeys.forEach(key => {
          if (key === configItem.key) {
            configItem.value = this[key]
          }
        })
      })
      this.$store.dispatch(CONFIG_ITEMS_EDIT, this.configItems)
    },

    saveLivingOptions () {
      this.saveConfig()
      this.livingOptions.forEach(livingOption => {
        this.$store.dispatch('SAVE_LIVING_OPTION', livingOption)
      })
    },

    saveLivingOption (livingOption) {
      this.$store.dispatch('SAVE_LIVING_OPTION', livingOption)

      // reset parent property on removed subsections
      const matchedSubsections = this.subsections.filter(subsection => subsection.parent.id === livingOption._id)
      const removedSubsectionsIds = matchedSubsections.filter(subsection => {
        if (!includes(livingOption.subsectionIds, subsection._id)) {
          return true
        }
      }).map(subsection => subsection._id)

      removedSubsectionsIds.forEach(subsectionId => {
        const subsection = this.subsections.find(subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          parent: {
            id: null,
            type: ''
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })

      // save subsections
      livingOption.subsectionIds.forEach(subsectionId => {
        const subsection = this.subsections.find(subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id,
          marker: subsection.marker,
          polygon: subsection.polygon,
          parent: {
            id: livingOption._id,
            type: 'livingOption'
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })

      this.viewAll()
    },

    async setData () {
      // Get the config items from the API
      this.configItems = await this.$store.dispatch(FETCH_CONFIG_ITEMS)

      // Reduce to just the live config items
      const liveConfigItems = filter(this.configItems, item => {
        return this.livingOptionsConfigItemsKeys.includes(item.key)
      })

      // Map the live items to the component data
      liveConfigItems.forEach(item => {
        this[item.key] = item.value
      })
    },

    addMasterplanTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.liveOptionConfigTags.value.push(res.data)
      })
    },

    addLivingOptionTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.activeLivingOption.tags.push(res.data)
      })
    },
  },
}
</script>
